/**
 * Print-ready QR download: asks for the printed size, then fetches a CMYK
 * TIFF (black as 100% K, 300 DPI+, white border included) from the backend
 * for placing in InDesign, with the code's logo (e.g. the gates) unless unticked.
 */

import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiV1 } from '@/api/client';

// Mirrors the backend limits in qrcodes/router.py and print_export.py.
const MIN_MM = 10;
const MAX_MM = 500;
const MIN_RECOMMENDED_MM = 20;
const DEFAULT_MM = 25;

const filenameFrom = (disposition, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(disposition || '');
  return match ? match[1] : fallback;
};

export default function PrintReadyQRDialog({ open, onOpenChange, qrId, qrName, hasLogo = false }) {
  const [sizeMm, setSizeMm] = useState(String(DEFAULT_MM));
  const [includeLogo, setIncludeLogo] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const size = Number(sizeMm);
  const valid = Number.isFinite(size) && size >= MIN_MM && size <= MAX_MM;
  const tooSmall = valid && size < MIN_RECOMMENDED_MM;

  const handleDownload = async () => {
    if (!valid) return;
    setDownloading(true);
    try {
      const logoParam = hasLogo && !includeLogo ? '&include_logo=false' : '';
      const response = await apiV1.get(`/qrcodes/${qrId}/print?size_mm=${size}${logoParam}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(
        response.headers?.['content-disposition'],
        `${qrName || 'qr-code'}-${size}mm-cmyk.tif`,
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Print-ready QR downloaded');
      onOpenChange(false);
    } catch (error) {
      console.error('Print-ready QR download failed:', error);
      toast.error('Failed to download print-ready QR');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Print-ready QR code</DialogTitle>
          <DialogDescription>
            CMYK TIFF at 300 DPI or higher, for placing in InDesign. Black is 100% K only,
            on white, without the screen colours.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="print-size-mm">Printed width of the code (mm)</Label>
          <Input
            id="print-size-mm"
            type="number"
            min={MIN_MM}
            max={MAX_MM}
            step="0.5"
            value={sizeMm}
            onChange={(e) => setSizeMm(e.target.value)}
            data-testid="print-size-mm"
          />
          <p className="text-xs text-muted-foreground">
            A white border (4 modules) is added around the code, so the file is a little larger.
            Place it at 100% in InDesign; don&apos;t enlarge it.
          </p>
          {!valid && (
            <p className="text-sm text-destructive" data-testid="print-size-invalid">
              Enter a size between {MIN_MM} and {MAX_MM} mm.
            </p>
          )}
          {tooSmall && (
            <p className="flex items-start gap-2 text-sm text-amber-600" data-testid="print-size-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Below {MIN_RECOMMENDED_MM} mm some phones may struggle to scan it.
            </p>
          )}
        </div>

        {hasLogo && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="print-include-logo"
              checked={includeLogo}
              onCheckedChange={(checked) => setIncludeLogo(checked === true)}
              data-testid="print-include-logo"
            />
            <Label htmlFor="print-include-logo" className="font-normal">
              Include the logo in the centre
            </Label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={!valid || downloading} data-testid="print-download-btn">
            {downloading
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            Download TIFF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
